import { useQuery } from '@tanstack/react-query';
import { getErrors, getErrorSessions } from '../../api/client';
import { formatDateTime, formatRelativeTime, formatDurationMs, formatNumber } from '../../utils/formatters';
import {
  ArrowLeft,
  AlertTriangle,
  BarChart3,
} from 'lucide-react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { useNavigate } from 'react-router-dom';

interface ErrorDetailProps {
  errorId: string;
  onBack: () => void;
}

export default function ErrorDetail({ errorId, onBack }: ErrorDetailProps) {
  const navigate = useNavigate();

  const { data: errorsData } = useQuery({
    queryKey: ['errors', 'all'],
    queryFn: () => getErrors({ pageSize: 100 }),
  });

  const error = (errorsData?.data || []).find((e) => (e.id || e.errorGroupHash) === errorId);

  const { data: sessions = [] } = useQuery({
    queryKey: ['error-sessions', errorId],
    queryFn: () => getErrorSessions(errorId),
  });

  // Generate mock occurrence chart data
  const occurrenceData = Array.from({ length: 7 }, (_, i) => ({
    date: new Date(Date.now() - (6 - i) * 86400000).toISOString().split('T')[0],
    count: Math.floor(Math.random() * (error?.count ?? error?.occurrences ?? 10) / 7),
  }));

  if (!error) {
    return (
      <div className="card flex h-64 items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-brand-200 border-t-brand-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <button
        onClick={onBack}
        className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-700"
      >
        <ArrowLeft className="h-4 w-4" />
        Back to Errors
      </button>

      {/* Error Header */}
      <div className="card">
        <div className="flex items-start gap-3">
          <div className="rounded-lg bg-red-100 p-2">
            <AlertTriangle className="h-5 w-5 text-red-600" />
          </div>
          <div className="flex-1">
            <h2 className="text-lg font-semibold text-gray-900">{error.message}</h2>
            <div className="mt-2 flex flex-wrap items-center gap-3 text-sm text-gray-500">
              <span className="badge bg-gray-100 text-gray-600">{error.type || error.errorType || 'Error'}</span>
              <span>{formatNumber(error.count ?? error.occurrences ?? 0)} occurrences</span>
              <span>{formatNumber(error.sessionsAffected ?? error.affectedSessions ?? 0)} sessions affected</span>
              <span>First seen {formatRelativeTime(error.firstSeen)}</span>
              <span>Last seen {formatRelativeTime(error.lastSeen)}</span>
            </div>
            <p className="mt-2 text-sm text-gray-500">{error.url || error.urlPath || ''}</p>
          </div>
        </div>

        {/* Stack Trace */}
        {(error.stack || error.latestStack) && (
          <div className="mt-4">
            <h3 className="mb-2 text-sm font-semibold text-gray-700">Stack Trace</h3>
            <pre className="overflow-x-auto rounded-lg bg-gray-900 p-4 text-xs text-gray-300 font-mono leading-relaxed">
              {error.stack || error.latestStack}
            </pre>
          </div>
        )}
      </div>

      {/* Occurrences Chart */}
      <div className="card">
        <div className="mb-4 flex items-center gap-2">
          <BarChart3 className="h-4 w-4 text-gray-400" />
          <h3 className="text-sm font-semibold text-gray-900">Occurrences Over Time</h3>
        </div>
        <ResponsiveContainer width="100%" height={200}>
          <BarChart data={occurrenceData}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis dataKey="date" tick={{ fontSize: 12 }} stroke="#9ca3af" />
            <YAxis tick={{ fontSize: 12 }} stroke="#9ca3af" />
            <Tooltip
              contentStyle={{
                borderRadius: '8px',
                border: '1px solid #e5e7eb',
              }}
            />
            <Bar dataKey="count" fill="#ef4444" radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Affected Sessions */}
      <div className="card">
        <h3 className="mb-4 text-sm font-semibold text-gray-900">Affected Sessions</h3>
        <div className="overflow-hidden rounded-lg border border-gray-200">
          <table className="min-w-full divide-y divide-gray-200">
            <thead>
              <tr>
                <th className="table-header">Visitor</th>
                <th className="table-header">Date</th>
                <th className="table-header">Duration</th>
                <th className="table-header">Device</th>
                <th className="table-header">Country</th>
                <th className="table-header">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {sessions.map((session: any) => (
                <tr key={session.id} className="hover:bg-gray-50">
                  <td className="table-cell font-mono text-xs">
                    {(session.visitorId || '').slice(0, 8)}
                  </td>
                  <td className="table-cell text-gray-500">
                    {formatDateTime(session.startedAt)}
                  </td>
                  <td className="table-cell">{formatDurationMs(session.durationMs ?? (session.duration ? session.duration * 1000 : 0))}</td>
                  <td className="table-cell capitalize">{session.device || session.deviceType || 'unknown'}</td>
                  <td className="table-cell">{session.country || '—'}</td>
                  <td className="table-cell">
                    <button
                      onClick={() => navigate(`/sessions/${session.id}`)}
                      className="text-sm font-medium text-brand-600 hover:text-brand-700"
                    >
                      Watch Replay
                    </button>
                  </td>
                </tr>
              ))}
              {sessions.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-6 py-8 text-center text-sm text-gray-500">
                    No affected sessions found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
